import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DxFileUploaderComponent} from 'devextreme-angular';
import {OAuthService} from 'angular-oauth2-oidc';
import {UserIdleService} from 'angular-user-idle';
import {TranslateService} from '@ngx-translate/core';
import {Message} from 'app/Model/System/Message';
import {LoginService} from 'app/Services/login.service';
import {CatalogService} from 'app/Services/CatalogManagement/catalog.service';
import {SystemService} from 'app/Services/system.service';

import {FtpServerService} from 'app/Services/ftp/ftpServer.service';
import {FtpServer} from 'app/Model/FtpServer';
import {FtpImportJobService} from 'app/Services/ftp/ftpImportJob.service';
import {FtpImportJobSettings} from 'app/Model/recurringJobs/ftp/ftpImportJobSettings';
import {switchMap} from 'rxjs/operators';

type Page = { id: number; title: string; template: 'upload' | 'ftp' };

@Component({
  selector: 'np-container-import',
  templateUrl: './container-import.component.html',
  styleUrls: ['./container-import.component.css'],
  standalone: false
})
export class ContainerImportComponent implements OnInit {
  private _visible = false;

  @Input()
  public set visible(val: boolean) {
    if (this._visible !== val) {
      this._visible = val;
      this.visibleChange.emit(val);
    }
  }

  public get visible(): boolean {
    return this._visible;
  }

  @Input() stage: string;
  @Input() containerId: string;
  @Input() lastCatalogName: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() importCompleted = new EventEmitter<void>();

  @ViewChild('dxUploader', { static: false })
  dxFileUploader?: DxFileUploaderComponent;

  fullScreen = false;
  isSaving = false;

  readonly uploadUrl = 'api/import/ImportCatalogChunky';
  readonly chunkSize = 10_000_000;
  readonly fileTypes = '.xml,.zip,.xlsx,.csv,.xls';
  uploadHeaders: Record<string, string> = {};

  pages: Page[] = [
    { id: 0, title: 'Datei hochladen', template: 'upload' },
    { id: 1, title: 'Automatischer FTP Import', template: 'ftp' }
  ];

  selectedTabIndex = 0;

  importMode: 'import' | 'compare' = 'compare';
  importModeItems: { value: 'import' | 'compare'; text: string }[] = [];

  hasFileSelected = false;

  ftpServers: FtpServer[] = [];
  connectionItems: FtpServer[] = [];
  selectedConnectionId: number | null = null;
  selectedFtpServer: FtpServer | null = null;

  importFolderItems: string[] = [];
  selectedImportFolderId: string | null = null;

  isConnected = false;
  isAutomatic = false;
  intervalItems: string[] = [];
  selectedInterval: string | null = null;
  hour = 15;
  minute = 30;
  nextImport?: Date;

  isLoadingFolders = false;

  constructor(
    private readonly oauth: OAuthService,
    private readonly userIdle: UserIdleService,
    public readonly translate: TranslateService,
    public readonly loginService: LoginService,
    public readonly catalogService: CatalogService,
    public readonly systemService: SystemService,
    private readonly ftpServerService: FtpServerService,
    private readonly ftpImportJobService: FtpImportJobService
  ) {}

  ngOnInit(): void {
    this.initHeaders();
    this.initPages();
    this.initImportModeItems();
    this.initFtpData();
  }

  private initHeaders(): void {
    const customerId = this.loginService.currentUser.customerId;
    const headers: Record<string, string> = { CustomerId: String(customerId) };

    if (this.containerId) {
      headers.ContainerId = String(this.containerId);
    }

    this.uploadHeaders = headers;
    this.applyCompareHeader();
  }

  private applyCompareHeader(): void {
    const headers: Record<string, string> = { ...this.uploadHeaders };

    if (this.importMode === 'compare') {
      headers.compareCatalog = String('true');
    } else {
      delete headers.compareCatalog;
    }

    this.uploadHeaders = headers;
  }

  private initPages(): void {
    const tUpload = this.translate.instant('Datei hochladen');
    const tFtp = this.translate.instant('Automatischer FTP Import');
    this.pages = [
      { id: 0, title: tUpload, template: 'upload' },
      { id: 1, title: tFtp, template: 'ftp' }
    ];
  }

  private initImportModeItems(): void {
    const compareLabel = this.lastCatalogName
      ? `${this.translate.instant('Vergleichen mit Katalog')}: ${this.lastCatalogName}`
      : this.translate.instant('Vergleichen mit Katalog');

    this.importModeItems = [
      {
        value: 'compare',
        text: compareLabel
      },
      {
        value: 'import',
        text: this.translate.instant('Nur importieren (sichtbar im Katalogarchiv)')
      }
    ];
  }

  private initFtpData(): void {
    this.intervalItems = [
      this.translate.instant('Täglich'),
      this.translate.instant('Wöchentlich'),
      this.translate.instant('Monatlich')
    ];
    this.selectedInterval = this.intervalItems[0] ?? null;

    this.ftpServerService.getAllServer().subscribe(servers => {
      this.ftpServers = servers ?? [];
      this.connectionItems = this.ftpServers;
    });
  }

  getTitle(): string {
    return this.translate.instant('Neuer Katalog Import');
  }

  onBeforeSendUpload(e: { request: XMLHttpRequest }): void {
    e.request.withCredentials = true;
    e.request.setRequestHeader('Authorization', 'Bearer ' + this.oauth.getAccessToken());
  }

  onUploadProgress(): void {
    this.userIdle.stopWatching();
    this.userIdle.startWatching();
  }

  onFileSelectionChanged(event: { value?: File[] }): void {
    this.hasFileSelected = !!event?.value && event.value.length > 0;
  }

  onImportModeChanged(event: { value: 'import' | 'compare' }): void {
    this.importMode = event.value;
    this.applyCompareHeader();
  }

  onUploaded(): void {
    this.dxFileUploader?.instance.reset();
    this.hasFileSelected = false;

    const text = this.translate.instant('Katalog hochgeladen');
    this.systemService.notify(new Message(text));

    this.finishAndOpenJobs();
  }

  onCancel = (): void => {
    this.visible = false;
  };

  onImportClick = (): void => {
    if (this.selectedTabIndex === 0) {
      this.dxFileUploader?.instance.upload();
      return;
    }

    if (!this.selectedFtpServer || !this.selectedConnectionId) {
      this.systemService.notify(
        new Message(this.translate.instant('Bitte einen FTP-Server auswählen.'))
      );
      return;
    }

    if (!this.selectedImportFolderId) {
      this.systemService.notify(
        new Message(this.translate.instant('Bitte einen Import-Ordner auswählen.'))
      );
      return;
    }

    const ftpJob: FtpImportJobSettings = {
      ftpServerId: this.selectedConnectionId,
      importPath: this.selectedImportFolderId,
      deleteImportFile: false,
      recursivePath: false,
      onlyUpdate: this.importMode === 'compare',
      containerId: this.containerId,
      compareCatalogBool: this.importMode === 'compare'
    } as any;

    this.isSaving = true;

    this.ftpImportJobService
      .create(ftpJob)
      .pipe(
        switchMap(job =>
          this.ftpImportJobService.run(
            (job as any).id,
            this.containerId,
            this.importMode === 'compare'
          )
        )
      )
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.finishAndOpenJobs();
        },
        error: () => {
          this.isSaving = false;
          this.systemService.notify(
            new Message(this.translate.instant('FTP Import konnte nicht gestartet werden.'))
          );
        }
      });
  };

  onConnectionChanged(event: { value: number }): void {
    this.selectedConnectionId = event.value;
    this.selectedFtpServer =
      this.ftpServers.find(s => s.id === event.value) ?? null;

    this.isConnected = false;
    this.importFolderItems = [];
    this.selectedImportFolderId = null;
  }

  onConnectClick(): void {
    if (!this.selectedFtpServer) {
      this.systemService.notify(
        new Message(this.translate.instant('Bitte zuerst einen FTP-Server auswählen.'))
      );
      return;
    }

    this.isLoadingFolders = true;

    this.ftpServerService.getFolderStructure(this.selectedFtpServer).subscribe({
      next: folders => {
        this.importFolderItems = folders ?? [];
        this.isConnected = true;
        this.isLoadingFolders = false;

        const defaultFolder = this.selectedFtpServer.importDirectory;
        if (defaultFolder && this.importFolderItems.includes(defaultFolder)) {
          this.selectedImportFolderId = defaultFolder;
        } else {
          this.selectedImportFolderId = this.importFolderItems[0] ?? null;
        }
      },
      error: () => {
        this.isConnected = false;
        this.isLoadingFolders = false;
        this.systemService.notify(
          new Message(this.translate.instant('Ordnerstruktur konnte nicht geladen werden.'))
        );
      }
    });
  }

  onAutomaticChanged(event: { value: boolean }): void {
    this.isAutomatic = event.value;
    this.recalculateNextImport();
  }

  onIntervalChanged(event: { value: string }): void {
    this.selectedInterval = event.value;
    this.recalculateNextImport();
  }

  onHourChanged(event: { value: number }): void {
    this.hour = event.value;
    this.recalculateNextImport();
  }

  onMinuteChanged(event: { value: number }): void {
    this.minute = event.value;
    this.recalculateNextImport();
  }

  onImportFolderChanged(event: { value: string }): void {
    this.selectedImportFolderId = event.value;

    if (!this.selectedFtpServer || !event.value) {
      return;
    }

    this.selectedFtpServer.importDirectory = event.value;
    this.ftpServerService.update(this.selectedFtpServer).subscribe();
  }

  private recalculateNextImport(): void {
    if (!this.isAutomatic) {
      this.nextImport = undefined;
      return;
    }

    const now = new Date();
    const next = new Date();
    next.setSeconds(0, 0);
    next.setHours(this.hour ?? 0, this.minute ?? 0);

    if (next <= now) {
      next.setDate(next.getDate() + 1);
    }

    this.nextImport = next;
  }

  private finishAndOpenJobs(): void {
    this.catalogService.viewService.jobsVisible = true;
    this.visible = false;
    this.importCompleted.emit();
  }
}
